import { useEffect, useRef, useState } from 'react';
import { useAppStore } from './store.js';

/** Millisekunden bis `endsAt`, auf Basis der geschätzten Serverzeit (GDD §28). */
export function remainingMs(endsAt: number | null): number {
  if (endsAt === null) return 0;
  const offset = useAppStore.getState().clockOffsetMs;
  return Math.max(0, endsAt - (Date.now() + offset));
}

/**
 * Liefert die verbleibenden Sekunden bis `endsAt`, per `requestAnimationFrame`
 * aktualisiert. rAF statt `setInterval`, damit der Wert nach dem Aufwachen aus
 * dem Sperrbildschirm sofort korrekt springt, statt erst nachzuticken.
 *
 * Der State wird nur bei einem echten Sekundenwechsel gesetzt und die Schleife
 * endet bei 0 — sonst liefe während der gesamten Diskussionsphase eine
 * 60-Hz-Schleife für einen Wert, der sich einmal pro Sekunde ändert.
 */
export function useCountdown(endsAt: number | null): number {
  const [seconds, setSeconds] = useState(() => Math.ceil(remainingMs(endsAt) / 1000));
  const lastSeconds = useRef(seconds);

  useEffect(() => {
    if (endsAt === null) {
      lastSeconds.current = 0;
      setSeconds(0);
      return;
    }

    let frame = 0;
    const tick = () => {
      const next = Math.ceil(remainingMs(endsAt) / 1000);
      if (next !== lastSeconds.current) {
        lastSeconds.current = next;
        setSeconds(next);
      }
      if (next > 0) frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [endsAt]);

  return seconds;
}

/**
 * Fortschritt 0…1 der laufenden Phase, bei jedem Frame aktualisiert.
 *
 * Bewusst getrennt von `useCountdown`: der Ring soll flüssig laufen, während
 * die Ziffern daneben nur einmal pro Sekunde neu rendern.
 */
export function useSmoothFraction(endsAt: number | null, totalSeconds: number): number {
  const [fraction, setFraction] = useState(() => initialFraction(endsAt, totalSeconds));

  useEffect(() => {
    if (endsAt === null || totalSeconds <= 0) {
      setFraction(0);
      return;
    }

    let frame = 0;
    const tick = () => {
      const next = remainingMs(endsAt) / (totalSeconds * 1000);
      setFraction(Math.max(0, Math.min(1, next)));
      if (next > 0) frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [endsAt, totalSeconds]);

  return fraction;
}

function initialFraction(endsAt: number | null, totalSeconds: number): number {
  if (endsAt === null || totalSeconds <= 0) return 0;
  return Math.max(0, Math.min(1, remainingMs(endsAt) / (totalSeconds * 1000)));
}

/**
 * Millisekunden seit `since`. `untilMs` beendet die Schleife, sobald der Wert
 * nicht mehr gebraucht wird — ohne die Grenze liefe sie die ganze Phase über
 * weiter und würde den Bildschirm 60-mal pro Sekunde neu rendern.
 */
export function useElapsedMs(since: number, untilMs = Number.POSITIVE_INFINITY): number {
  const [elapsed, setElapsed] = useState(() => Math.max(0, elapsedMsSince(since)));

  useEffect(() => {
    let frame = 0;
    const tick = () => {
      const next = Math.max(0, elapsedMsSince(since));
      setElapsed(next);
      if (next < untilMs) frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [since, untilMs]);

  return elapsed;
}

function elapsedMsSince(since: number): number {
  const offset = useAppStore.getState().clockOffsetMs;
  return Date.now() + offset - since;
}
